import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL, URLS } from "../../_config";
import { useRole, useToken } from "../../_common/hooks/authHook";
import { showDate } from "../../_common/functions";
import Nav from "../../components/layouts/nav";
import Footer from "../../components/layouts/footer";

const CommissionHistory = () => {
    const tokenData: any = useToken();
    const userRole: any = useRole();
    const navigate: any = useNavigate();

    const [summary, setSummary] = useState<Record<string, any> | null>(null);
    const [commissions, setCommissions] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (userRole && userRole !== 'marketer') {
            navigate(URLS.ACCOUNT.MY_BUSINESS_PLAN);
        }
    }, [userRole]);

    const fetchCommissions = async (page: number = 1) => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL.MARKETER.COMMISSIONS}?page=${page}&per_page=20`, {
                headers: { Authorization: `Bearer ${tokenData}` },
            });
            const resp = await response.json();
            if (resp?.data) {
                setSummary(resp.data.summary);
                setCommissions(resp.data.commissions);
            }
        } catch (error) {
            console.error('Error fetching commission history:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tokenData) {
            fetchCommissions(currentPage);
        }
    }, [currentPage, tokenData]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const CURRENCY_SYMBOLS: Record<string, string> = { USD: '$', CAD: 'C$' };

    const formatAmount = (amount: number, currency: string) => {
        const symbol = CURRENCY_SYMBOLS[currency?.toUpperCase()] ?? currency ?? '$';
        return `${symbol}${Number(amount).toFixed(2)}`;
    };

    const statusBadge = (status: string) => {
        if (status === 'paid') {
            return <span className="badge bg-success">Paid</span>;
        }
        return <span className="badge bg-warning text-dark">Eligible</span>;
    };

    return (
        <>
            <Nav />
            <div className="body-part gray-bg mt-105">
               
            {/* <section className="my-account-section section-padding"> */}
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center">
                            <h2 className="section-title mb-4">Commission History</h2>
                        </div>
                    </div>

                    {/* Summary Cards — grouped by currency */}
                    {summary && Object.entries(summary).map(([currency, data]: [string, any]) => (
                        <div key={currency} className="mb-3">
                            {Object.keys(summary).length > 1 && (
                                <h6 className="text-muted mb-2 fw-semibold">{currency}</h6>
                            )}
                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <div className="card text-center shadow-sm h-100">
                                        <div className="card-body">
                                            <h6 className="card-subtitle mb-2 text-muted">Total Balance</h6>
                                            <h3 className="card-title text-primary">{formatAmount(data.total_balance, currency)}</h3>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <div className="card text-center shadow-sm h-100">
                                        <div className="card-body">
                                            <h6 className="card-subtitle mb-2 text-muted">Current Balance</h6>
                                            <h3 className="card-title text-success">{formatAmount(data.current_balance, currency)}</h3>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <div className="card text-center shadow-sm h-100">
                                        <div className="card-body">
                                            <h6 className="card-subtitle mb-2 text-muted">Due Pay Out</h6>
                                            <h3 className="card-title text-warning">{formatAmount(data.due_amount, currency)}</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Commission Table */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-body p-0">
                                    {loading ? (
                                        <div className="text-center py-5">Loading...</div>
                                    ) : commissions?.data?.length === 0 ? (
                                        <div className="text-center py-5 text-muted">No commissions yet.</div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Proposal ID / Name</th>
                                                        <th>Plan Details</th>
                                                        <th>Commission Amount</th>
                                                        <th>Status</th>
                                                        <th>Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {commissions?.data?.map((commission: any, index: number) => (
                                                        <tr key={commission.id}>
                                                            <td>{((currentPage - 1) * (commissions.per_page || 20)) + index + 1}</td>
                                                            <td>{commission.proposal?.proposal_no || `#${commission.proposal_id}`}</td>
                                                            <td>{commission.proposal?.subscription_plan?.title || '—'}</td>
                                                            <td>{formatAmount(commission.calculated_amount ?? commission.commission_amount, commission.proposal?.currency)}</td>
                                                            <td>{statusBadge(commission.status)}</td>
                                                            <td>{showDate(commission.created_at)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex justify-content-start mt-2">
                                <p className="text-muted fw-normal">Total: {commissions?.total}</p>
                            </div>

                            {/* Pagination */}
                            {commissions && commissions.last_page > 1 && (
                                <div className="d-flex justify-content-center mt-4">
                                    <nav>
                                        <ul className="pagination">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                                            </li>
                                            {Array.from({ length: commissions.last_page }, (_, i) => i + 1).map(page => (
                                                <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                                    <button className="page-link" onClick={() => handlePageChange(page)}>{page}</button>
                                                </li>
                                            ))}
                                            <li className={`page-item ${currentPage === commissions.last_page ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            {/* </section> */}
            </div>
            <Footer />
        </>
    );
};

export default CommissionHistory;
